import { getMonthName, stringToMonthYear } from '@common/neo.tax';

import { AscPeriodLockStatusFragment } from '@neo.tax/shared';

/** Beyond this many open months, naming the locked ones instead reads shorter. */
const SUMMARISE_ABOVE_OPEN_MONTHS = 3;

/** "January", "January and February", "January, February and March" */
const formatMonthList = (monthNames: string[]): string => {
  if (monthNames.length === 1) {
    return monthNames[0];
  }
  return `${monthNames.slice(0, -1).join(', ')} and ${monthNames[monthNames.length - 1]}`;
};

const toMonthName = (monthYear: string): string => getMonthName(stringToMonthYear(monthYear).month, 'long');

/**
 * A locked period that still holds open months, which is what a partial lock looks like from above:
 * locking Q1 leaves H1 locked with April to June open, and releasing February out of a locked Q1
 * leaves Q1 locked with February open. Either way the page is read-only, since a save would reach
 * the closed months.
 *
 * A period reads as locked while any month under it is, so the converse cannot happen: a period
 * holding a closed month is never itself unlocked.
 */
export const isPeriodPartiallyLocked = (lockStatus: AscPeriodLockStatusFragment): boolean =>
  lockStatus.isLocked && lockStatus.monthLockStates.some((month) => !month.isLocked);

/**
 * Names the open months of a locked period. Returns null for a period that is wholly locked, wholly
 * open, or is itself a month.
 *
 * Listing the open ones is clearest until there are many, at which point naming the closed ones is
 * shorter — a year locked through a single month would otherwise list the other eleven.
 */
export const getMonthLockExceptionsText = (lockStatus: AscPeriodLockStatusFragment): string | null => {
  if (!isPeriodPartiallyLocked(lockStatus)) {
    return null;
  }

  const openMonths = lockStatus.monthLockStates.filter((month) => !month.isLocked);
  // Non-empty: the period reads as locked, so at least one of its months is closed.
  const lockedMonths = lockStatus.monthLockStates.filter((month) => month.isLocked);

  if (openMonths.length > SUMMARISE_ABOVE_OPEN_MONTHS && lockedMonths.length < openMonths.length) {
    return `Only ${formatMonthList(lockedMonths.map((month) => toMonthName(month.monthYear)))} ${
      lockedMonths.length === 1 ? 'is' : 'are'
    } locked.`;
  }

  return `${formatMonthList(openMonths.map((month) => toMonthName(month.monthYear)))} ${
    openMonths.length === 1 ? 'is' : 'are'
  } still open.`;
};
